// src/components/MainApp.jsx with updated customer routes
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Box, CircularProgress, Typography, alpha } from '@mui/material';
import NavigationBar from './NavigationBar';
import NavigationMenu from './NavigationMenu';
import { PermissionManager, usePermissions } from '../utils/permissions';
import DashboardPage from '../pages/dashboard/DashboardPage';
import LocationsPage from '../pages/locations/LocationsPage';
import EquipmentList from '../pages/locations/EquipmentList';
import EquipmentMaintenance from '../pages/locations/EquipmentMaintenance';
import SettingsPage from '../pages/settings/SettingsPage';
import CompanyCreationPage from '../pages/CompanyCreationPage';
import InvoicesPage from '../pages/crm/InvoicesPage';
import InvoiceCreation from '../pages/crm/invoice-components/InvoiceCreation';
import ProfileSetup from './ProfileSetup';
import CompanyDisabled from './CompanyDisabled';
import '../LayoutFix.css'; 

// CRM pages
import CRMOverviewPage from '../pages/crm/CRMOverviewPage';
import CustomersListPage from '../pages/crm/CustomerListPage';
import CustomerForm from '../pages/crm/CustomerForm';

// Purchase Order pages
import PurchaseOrdersPage from '../pages/crm/ProformaInvoices';
import PurchaseOrdersTabs from '../pages/crm/proforma-invoices-components/ProformaInvoicesTabs';
import PODeliveryConfirmation from '../pages/crm/proforma-invoices-components/PIDeliveryConfirmation';

// Product pages
import BrandsPage from '../pages/products/BrandsPage';
import ProductsPage from '../pages/products/ProductsPage';
import FormulationsPage from '../pages/formulations/FormulationsPage';
import ProductPriceListPage from '../pages/crm/ProductPriceListPage';

// Expense pages
import ExpensesPage from '../pages/expenses/ExpensesPage';
import AccountsPage from '../pages/expenses/AccountsPage';
import CategoryPage from '../pages/expenses/CategoryPage';
import AccountingOverviewPage from '../pages/accounting/AccountingOverviewPage';
import AccountingBankingPage from '../pages/accounting/AccountingBankingPage';
import AccountingReceivablesPage from '../pages/accounting/AccountingReceivablesPage';
import AccountingPayablesPage from '../pages/accounting/AccountingPayablesPage';
import AccountingForecastPage from '../pages/accounting/AccountingForecastPage';

// Supply Chain pages
import SuppliersPage from '../pages/vendors/SuppliersPage';
import VendorPurchaseOrdersPage from '../pages/vendors/PurchaseOrdersPage';
import VendorPurchaseOrderForm from '../pages/vendors/PurchaseOrderForm';
import QCTests from '../pages/inventory/qc-tests/QCTests';
import ImportsPage from '../pages/inventory/ImportsPage';
import ExportsPage from '../pages/inventory/ExportsPage';
import MasterInventoryPage from '../pages/inventory/InventoryPage';

// Manufacturing pages
import BatchingPage from '../pages/batch-production/BatchingPage';
import BatchProductionOverview from '../pages/batch-production/BatchProductionOverview';
import ProductionPlanning from '../pages/production-planning/ProductionPlanning';
import Requests from '../pages/production-planning/Requests';
import ProductionLinePage from '../pages/line-production/ProductionLinePage';
import ProductionLineOverviewPage from '../pages/line-production/ProductionLineOverviewPage';
import ProductionLineTemplates from '../pages/line-production/ProductionLineTemplates';

// Tasks pages
import Tasks from '../pages/tasks/Tasks';

// Calendar pages
import Calendar from '../pages/calendar/Calendar';

// Schedule pages
import Schedule from '../pages/schedule/Schedule';
import Analytics from '../pages/schedule/components/Analytics';
import EmployeeTasks from '../pages/schedule/components/EmployeeTasks';

// Employee pages
import Employees from '../pages/employees/Employees';
import EmployeeEditor from '../pages/employees/EmployeeEditor';

const MainApp = ({ user }) => {
  const { 
    companyId, 
    companyIsActive, 
    isOwner, 
    loading: permissionsLoading,
    refresh: refreshPermissions 
  } = usePermissions();
  
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(null);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [userStatus, setUserStatus] = useState('active'); // 'active', 'inactive', 'removed'
  const [forceCompanyCreation, setForceCompanyCreation] = useState(false);

  const updateLastLogin = async () => {
    // No longer needed since we're using simple allowed_users array
    return;
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        // SECURITY FIX: Clear any existing company state at start of initialization
        setCompanies([]);
        setCurrentCompany(null);
        
        // SECURITY FIX: Check for invitation_accepted parameter and clear cache
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('invitation_accepted') === 'true') {
          // Clear all cached data to force fresh company context load
          localStorage.removeItem('currentCompanyId');
          localStorage.removeItem('userPermissions');
          
          // Clean URL without reloading
          window.history.replaceState({}, document.title, '/');
        }
        
        // Always check profile completion first
          const { data: profileData } = await supabase.rpc('check_user_profile_complete');
          const isComplete = profileData?.[0]?.profile_complete || false;
          setProfileComplete(isComplete);
          
                // Only fetch companies if profile is complete and not forcing company creation
        if (isComplete) {
          if (forceCompanyCreation) {
            // Skip company checking and go straight to company creation
            setUserStatus('active');
            setCurrentCompany(null);
            setCompanies([]);
          } else {
            const companiesResult = await fetchCompanies();
            
            if (companiesResult === 'inactive') {
              setUserStatus('inactive');
              // SECURITY: Clear any cached company data immediately
              setCompanies([]);
              setCurrentCompany(null);
            } else if (companiesResult === 'removed') {
              setUserStatus('removed');
              // SECURITY: Clear any cached company data immediately
              setCompanies([]);
              setCurrentCompany(null);
            } else if (companiesResult === 'company_deactivated') {
              setUserStatus('company_deactivated');
              // SECURITY: Clear any cached company data immediately
              setCompanies([]);
              setCurrentCompany(null);
            } else if (companiesResult === true) {
              setUserStatus('active');
          // Update last_login for this user
          await updateLastLogin();
        } else {
              setUserStatus('active'); // Default for users with no companies (will show company creation)
            }
          }
        }
        
        setCheckingProfile(false);
        setLoading(false);
      } catch (error) {
        console.error('Error during initialization:', error);
        setCheckingProfile(false);
        setLoading(false);
      }
    };

    const fetchCompanies = async () => {
      try {
        // Verify user is still authenticated
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (!currentUser) {
          console.error('User not authenticated in fetchCompanies');
          return false;
        }
        
        // SECURITY FIX: Check user status FIRST before loading any company data
        const { data: allUserStatus, error: statusError } = await supabase
          .from('company_users')
          .select('status, company_id')
          .eq('user_id', currentUser.id);

        if (statusError) {
          console.error('Error checking user status:', statusError);
          return false;
        }

        // If user has no company memberships at all, they were removed
        if (!allUserStatus || allUserStatus.length === 0) {
          // IMMEDIATELY clear all company data for security
          setCompanies([]);
          setCurrentCompany(null);
          localStorage.removeItem('currentCompanyId');
          return 'removed'; // User was completely removed from all companies
        }

        // Check for active memberships
        const activeUserStatus = allUserStatus.filter(status => status.status === 'active');
        
        // If user has company memberships but none are active, they're inactive
        if (activeUserStatus.length === 0) {
          // IMMEDIATELY clear all company data for security
          setCompanies([]);
          setCurrentCompany(null);
          localStorage.removeItem('currentCompanyId');
          return 'inactive'; // User exists in companies but is inactive
        }
        
        // Get active company IDs
        const activeCompanyIds = activeUserStatus.map(status => status.company_id);
        
        // SECURITY: Only fetch companies for which user has ACTIVE membership
        // This prevents removed users from accessing company data
        const { data, error } = await supabase
          .from('companies')
          .select('*')
          .in('id', activeCompanyIds);

        if (error) {
          console.error('Error fetching companies:', error);
          return false;
        } else {
          if (!data || data.length === 0) {
            setCompanies([]);
            setCurrentCompany(null);
            return false; // No companies
          }

          // Check if the company is deactivated
          const currentCompany = data[0]; // For now assume single company
          if (currentCompany.is_active === false) {
            setCompanies([]);
            setCurrentCompany(null);
            return 'company_deactivated'; // New status for deactivated company
          }

          setCompanies(data || []);
          setCurrentCompany(data?.length ? data[0] : null);
          
          if (data?.length > 0) {
            await PermissionManager.initializeUserContext();
            return true; // Has companies
          }
          return false; // No companies
        }
      } catch (error) {
        console.error('Error in fetchCompanies:', error);
        return false;
      }
    };

    initialize();

    return () => {};
  }, [user.id, forceCompanyCreation]);

  // Load company data when companyId is available
  useEffect(() => {
    const loadCompanyData = async () => {
      if (companyId && !currentCompany) {
        try {
          const { data, error } = await supabase
            .from('companies')
            .select('*')
            .eq('id', companyId)
            .single();
          
          if (!error && data) {
            setCurrentCompany(data);
          }
        } catch (err) {
          console.error('Error loading company data:', err);
        }
      }
    };

    loadCompanyData();
  }, [companyId, currentCompany]);



  if (checkingProfile || loading || permissionsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Show company disabled page if company exists but is deactivated
  if (companyId && companyIsActive === false) {
    return (
      <CompanyDisabled
        companyName={currentCompany?.name || 'Your Company'}
        companyId={companyId}
        isOwner={isOwner}
        onReactivationSuccess={async () => {
          await refreshPermissions();
          // Just refresh permissions, don't reload the page
        }}
      />
    );
  }

  // Show profile setup if profile is not complete
  if (profileComplete === false) {
    return (
      <ProfileSetup
        currentUser={user}
        onProfileComplete={() => {
          // Reload the page to reinitialize and fetch companies
          window.location.href = '/';
        }}
      />
    );
  }

  // Show removed user page for users who were completely removed from company
  if (userStatus === 'removed') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: '#f7f7f8',
          position: 'relative'
        }}
      >
        {/* GlowWorks logo */}
        <Box sx={{ position: 'absolute', top: 32, left: 32, zIndex: 10 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 500,
              color: '#000',
              fontSize: '1.8rem',
              letterSpacing: '0.5px',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              cursor: 'default',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'scale(1.02)',
                textShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
              }
            }}
          >
            GlowWorks
          </Typography>
        </Box>

        <Box sx={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2
        }}>
          <Box
            sx={{
              width: '100%',
              maxWidth: '450px',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha('#636366', 0.2)}`,
              borderRadius: 3,
              padding: 5,
              boxShadow: `0 24px 64px ${alpha('#636366', 0.15)}`,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 32px 80px ${alpha('#636366', 0.2)}`,
                border: `1px solid ${alpha('#636366', 0.3)}`
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 6,
                background: 'linear-gradient(90deg, #636366, #8e8e93)'
              }
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 500,
                color: '#000',
                fontSize: '2rem',
                mb: 4,
                letterSpacing: '-0.5px',
                textAlign: 'center'
              }}
            >
              No Company Access
            </Typography>

            <Box
              sx={{
                mb: 3,
                p: 3,
                borderRadius: 1.5,
                backgroundColor: alpha('#000', 0.05),
                border: `1px solid ${alpha('#000', 0.12)}`,
                textAlign: 'center'
              }}
            >
              <Typography
                variant="body1"
                sx={{ 
                  color: 'text.primary', 
                  fontWeight: 500, 
                  lineHeight: 1.5, 
                  fontSize: '1rem',
                  mb: 2
                }}
              >
                You're not currently associated with any company.
              </Typography>
              <Typography
                variant="body2"
                sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 400, 
                  lineHeight: 1.5, 
                  fontSize: '0.9rem'
                }}
              >
                You can create a new company or wait for an invitation to join an existing one.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
              <Box 
                component="button"
                onClick={() => {
                  setForceCompanyCreation(true); // Force company creation bypassing removed status
                  setUserStatus('active');
                }}
                sx={{ 
                  width: '100%',
                  py: 2.5,
                  px: 4,
                  backgroundColor: '#000',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: '1rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: 'inherit',
                  '&:hover': {
                    backgroundColor: '#333',
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 16px ${alpha('#000', 0.3)}`
                  },
                  '&:active': {
                    transform: 'translateY(0)'
                  }
                }}
              >
                Create New Company
              </Box>
              
              <Box 
                component="button"
                onClick={() => supabase.auth.signOut()}
                sx={{ 
                  width: '100%',
                  py: 2.5,
                  px: 4,
                  backgroundColor: '#fff',
                  color: '#636366',
                  border: `2px solid ${alpha('#636366', 0.3)}`,
                  borderRadius: 4,
                  fontSize: '1rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: 'inherit',
                  '&:hover': {
                    backgroundColor: alpha('#636366', 0.05),
                    border: `2px solid ${alpha('#636366', 0.5)}`,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 16px ${alpha('#636366', 0.15)}`
                  },
                  '&:active': {
                    transform: 'translateY(0)'
                  }
                }}
              >
                Sign Out
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // Show blocked message for inactive users
  if (userStatus === 'inactive') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: '#f7f7f8',
          position: 'relative'
        }}
      >
        {/* GlowWorks logo */}
        <Box sx={{ position: 'absolute', top: 32, left: 32, zIndex: 10 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 500,
              color: '#000',
              fontSize: '1.8rem',
              letterSpacing: '0.5px',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              cursor: 'default',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'scale(1.02)',
                textShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
              }
            }}
          >
            GlowWorks
          </Typography>
        </Box>

        <Box sx={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2
        }}>
                  <Box
          sx={{
            width: '100%',
            maxWidth: '450px',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha('#636366', 0.2)}`,
            borderRadius: 3,
            padding: 5,
            boxShadow: `0 24px 64px ${alpha('#636366', 0.15)}`,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 32px 80px ${alpha('#636366', 0.2)}`,
              border: `1px solid ${alpha('#636366', 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 6,
              background: 'linear-gradient(90deg, #636366, #8e8e93)'
            }
          }}
        >
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 500,
              color: '#000',
              fontSize: '2rem',
              mb: 4,
              letterSpacing: '-0.5px',
              textAlign: 'center'
            }}
          >
            Account Inactive
          </Typography>

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 1.5,
              backgroundColor: alpha('#ef4444', 0.1),
              border: `1px solid ${alpha('#ef4444', 0.2)}`,
              textAlign: 'center'
            }}
          >
            <Typography
              variant="body1"
              sx={{ 
                color: '#ef4444', 
                fontWeight: 500, 
                lineHeight: 1.5, 
                fontSize: '1rem',
                mb: 2
              }}
            >
              Your account has been deactivated by an administrator.
            </Typography>
            <Typography
              variant="body2"
              sx={{ 
                color: alpha('#ef4444', 0.8), 
                fontWeight: 400, 
                lineHeight: 1.5, 
                fontSize: '0.9rem'
              }}
            >
              Please contact an administrator to reactivate your account and regain access to the system.
            </Typography>
          </Box>

          <Box 
            component="button"
            onClick={() => supabase.auth.signOut()}
            sx={{ 
              width: '100%',
              py: 2.5,
              px: 4,
              backgroundColor: '#fff',
              color: '#636366',
              border: `2px solid ${alpha('#636366', 0.3)}`,
              borderRadius: 4,
              fontSize: '1rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              fontFamily: 'inherit',
              '&:hover': {
                backgroundColor: alpha('#636366', 0.05),
                border: `2px solid ${alpha('#636366', 0.5)}`,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 16px ${alpha('#636366', 0.15)}`
              },
              '&:active': {
                transform: 'translateY(0)'
              }
            }}
          >
            Sign Out
          </Box>
        </Box>
        </Box>
      </Box>
    );
  }

  // Show company deactivated message for all users when company is deactivated
  if (userStatus === 'company_deactivated') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: '#f7f7f8',
          position: 'relative'
        }}
      >
        {/* GlowWorks logo */}
        <Box sx={{ position: 'absolute', top: 32, left: 32, zIndex: 10 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 500,
              color: '#000',
              fontSize: '1.8rem',
              letterSpacing: '0.5px',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              cursor: 'default',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'scale(1.02)',
                textShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
              }
            }}
          >
            GlowWorks
          </Typography>
        </Box>

        <Box sx={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2
        }}>
          <Box
            sx={{
              width: '100%',
              maxWidth: '450px',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha('#636366', 0.2)}`,
              borderRadius: 3,
              padding: 5,
              boxShadow: `0 24px 64px ${alpha('#636366', 0.15)}`,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 32px 80px ${alpha('#636366', 0.2)}`,
                border: `1px solid ${alpha('#636366', 0.3)}`
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 6,
                background: 'linear-gradient(90deg, #f59e0b, #d97706)'
              }
            }}
          >
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 500,
              color: '#000',
              fontSize: '2rem',
              mb: 4,
              letterSpacing: '-0.5px',
              textAlign: 'center'
            }}
          >
            Company Temporarily Unavailable
          </Typography>

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 1.5,
              backgroundColor: alpha('#f59e0b', 0.1),
              border: `1px solid ${alpha('#f59e0b', 0.2)}`,
              textAlign: 'center'
            }}
          >
            <Typography
              variant="body1"
              sx={{ 
                color: '#d97706', 
                fontWeight: 500, 
                lineHeight: 1.5, 
                fontSize: '1rem',
                mb: 2
              }}
            >
              The company has been temporarily deactivated by an administrator.
            </Typography>
            <Typography
              variant="body2"
              sx={{ 
                color: alpha('#d97706', 0.8), 
                fontWeight: 400, 
                lineHeight: 1.5, 
                fontSize: '0.9rem'
              }}
            >
              Please sign out and sign in again to access the system.
            </Typography>
          </Box>

          <Box 
            component="button"
            onClick={() => supabase.auth.signOut()}
            sx={{ 
              width: '100%',
              py: 2.5,
              px: 4,
              backgroundColor: '#fff',
              color: '#636366',
              border: `2px solid ${alpha('#636366', 0.3)}`,
              borderRadius: 4,
              fontSize: '1rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              fontFamily: 'inherit',
              '&:hover': {
                backgroundColor: alpha('#636366', 0.05),
                border: `2px solid ${alpha('#636366', 0.5)}`,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 16px ${alpha('#636366', 0.15)}`
              },
              '&:active': {
                transform: 'translateY(0)'
              }
            }}
          >
            Sign Out
          </Box>
        </Box>
        </Box>
      </Box>
    );
  }

  if (!currentCompany && profileComplete === true) {
    
    // Show company creation if user has complete profile but no companies
    return (
      <BrowserRouter>
        <Routes>
          <Route
            path="*"
            element={(
              <CompanyCreationPage
                onCompanyCreated={(newCompany) => {
                  setCompanies([newCompany]);
                  setCurrentCompany(newCompany);
                  setForceCompanyCreation(false); // Reset the flag after successful company creation
                }}
                onCancel={() => {
                  setForceCompanyCreation(false); // Go back to "No Company Access" screen
                }}
              />
            )}
          />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <CompanyLocationGuard currentCompany={currentCompany} />
      <Routes>
        <Route path="/" element={<Navigate to={`/company/${currentCompany.id}/menu`} />} />
        
        {/* Navigation menu route - main landing page */}
        <Route 
          path="/company/:companyId/menu" 
          element={
            <Box sx={{ 
              minHeight: '100vh',
              background: '#f5f5f5',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <NavigationMenu company={currentCompany} />
            </Box>
          } 
        />

        {/* All other routes - with top navigation */}
        <Route 
          path="/company/:companyId/*" 
          element={
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <NavigationBar
                company={currentCompany}
                onLogout={() => supabase.auth.signOut()}
              />
              <Box
                component="main"
                sx={{
                  flexGrow: 1,
                  pt: '80px',
                  pl: { xs: 2, sm: 4 },
                  pr: { xs: 2, sm: 4 },
                  minHeight: '100vh',
                  bgcolor: 'background.default',
                  margin: '0 auto',
                  marginLeft: 20,
                  width: '100%'
                }}
              >
                <Routes>
                  <Route
                    path="/dashboard"
                    element={<DashboardPage />}
                  />

                  <Route
                    path="/tasks"
                    element={<Tasks />}
                  />
                  
                  <Route
                    path="/tasks/edit/:taskId"
                    element={<Tasks />}
                  />
                  
                  <Route
                    path="/tasks/new"
                    element={<Tasks />}
                  />
                  
                  <Route
                    path="/calendar"
                    element={<Calendar />}
                  />
                  
                  <Route
                    path="/calendar/edit/:eventId"
                    element={<Calendar />}
                  />
                  
                  <Route
                    path="/calendar/edit/new"
                    element={<Calendar />}
                  />
                  
                  <Route
                    path="/schedule"
                    element={<Schedule />}
                  />
                  
                  <Route
                    path="/schedule/templates"
                    element={<Schedule />}
                  />
                  
                  <Route
                    path="/schedule/tasks"
                    element={<EmployeeTasks />}
                  />
                  
                  <Route
                    path="/schedule/analytics"
                    element={<Analytics />}
                  />
                  
                  <Route
                    path="/employees"
                    element={<Employees />}
                  />
                  
                  <Route
                    path="/employees/new"
                    element={<EmployeeEditor />}
                  />
                  
                  <Route
                    path="/employees/:employeeId"
                    element={<EmployeeEditor />}
                  />
                  
                  <Route
                    path="/locations"
                    element={(
                      <LocationsPage
                        currentCompany={currentCompany}
                        companies={companies}
                        onCompanyChange={setCurrentCompany}
                      />
                    )}
                  />
                  
                  <Route
                    path="/equipment-list"
                    element={(
                      <EquipmentList
                        currentCompany={currentCompany}
                        companies={companies}
                        onCompanyChange={setCurrentCompany}
                      />
                    )}
                  />
                  
                  <Route
                    path="/equipment-list/:departmentId"
                    element={(
                      <EquipmentList
                        currentCompany={currentCompany}
                        companies={companies}
                        onCompanyChange={setCurrentCompany}
                      />
                    )}
                  />

                  <Route
                    path="/equipment-maintenance"
                    element={(
                      <EquipmentMaintenance
                        currentCompany={currentCompany}
                        companies={companies}
                        onCompanyChange={setCurrentCompany}
                      />
                    )}
                  />
                  <Route
                    path="/equipment-maintenance/:departmentId"
                    element={(
                      <EquipmentMaintenance
                        currentCompany={currentCompany}
                        companies={companies}
                        onCompanyChange={setCurrentCompany}
                      />
                    )}
                  />
                  <Route
                    path="/equipment-maintenance/:departmentId/:equipmentId"
                    element={(
                      <EquipmentMaintenance
                        currentCompany={currentCompany}
                        companies={companies}
                        onCompanyChange={setCurrentCompany}
                      />
                    )}
                  />

                  <Route
                    path="/transactions"
                    element={<Navigate to={`/company/${currentCompany.id}/locations`} />}
                  />

                  {/* Proforma Invoices routes */}
                  <Route
                    path="/proforma-invoices"
                    element={<PurchaseOrdersPage />}
                  />

                  <Route
                    path="/proforma-invoices/new"
                    element={<PurchaseOrdersTabs />}
                  />

                  <Route
                    path="/proforma-invoices/:id"
                    element={<PurchaseOrdersTabs />}
                  />

                  <Route
                    path="/proforma-invoices/:id/delivery-confirmation"
                    element={<PODeliveryConfirmation />}
                  />

                  {/* Invoices routes */}
                  <Route
                    path="/invoices"
                    element={<InvoicesPage currentCompany={currentCompany} />}
                  />

                  <Route
                    path="/invoices/new"
                    element={<InvoiceCreation currentCompany={currentCompany} />}
                  />

                  <Route
                    path="/invoices/edit/:id"
                    element={<InvoiceCreation currentCompany={currentCompany} />}
                  />

                  <Route
                    path="/settings"
                    element={<SettingsPage currentCompany={currentCompany} />}
                  />

                  {/* CRM routes */}
                  <Route
                    path="/crm-overview"
                    element={<CRMOverviewPage />}
                  />

                  <Route
                    path="/customers-list"
                    element={<CustomersListPage />}
                  />

                  <Route
                    path="/customer-new"
                    element={<CustomerForm currentCompany={currentCompany} />}
                  />

                  <Route
                    path="/customer-edit/:customerId"
                    element={<CustomerForm currentCompany={currentCompany} />}
                  />

                  {/* Product routes */}
                  <Route
                    path="/products"
                    element={<ProductsPage currentCompany={currentCompany} />}
                  />
                  <Route
                    path="/brands"
                    element={<BrandsPage currentCompany={currentCompany} />}
                  />
                  <Route
                    path="/price-lists"
                    element={<ProductPriceListPage />}
                  />

                  {/* Expense routes */}
                  <Route
                    path="/expenses"
                    element={<ExpensesPage />}
                  />
                  <Route
                    path="/expenses/accounts"
                    element={<AccountsPage />}
                  />
                  <Route
                    path="/expenses/categories"
                    element={<CategoryPage />}
                  />

                  <Route
                    path="/accounting"
                    element={<Navigate to={`/company/${currentCompany.id}/accounting/overview`} replace />}
                  />
                  <Route
                    path="/accounting/overview"
                    element={<AccountingOverviewPage />}
                  />
                  <Route
                    path="/accounting/banking"
                    element={<AccountingBankingPage />}
                  />
                  <Route
                    path="/accounting/receivables"
                    element={<AccountingReceivablesPage />}
                  />
                  <Route
                    path="/accounting/payables"
                    element={<AccountingPayablesPage />}
                  />
                  <Route
                    path="/accounting/forecast"
                    element={<AccountingForecastPage />}
                  />

                  <Route
                    path="/formulations"
                    element={<FormulationsPage currentCompany={currentCompany} />}
                  />

                  {/* Supply Chain routes */}
                  <Route
                    path="/vendors"
                    element={<SuppliersPage currentCompany={currentCompany} />}
                  />
                  <Route
                    path="/purchase-orders"
                    element={<VendorPurchaseOrdersPage currentCompany={currentCompany} />}
                  />
                  <Route
                    path="/purchase-orders/new"
                    element={<VendorPurchaseOrderForm currentCompany={currentCompany} />}
                  />
                  <Route
                    path="/purchase-orders/:poId"
                    element={<VendorPurchaseOrderForm currentCompany={currentCompany} />}
                  /> 
                  
                  <Route
                    path="/inventory/imports"
                    element={<ImportsPage />}
                  />
                  
                  <Route
                    path="/inventory/imports/:category"
                    element={<ImportsPage />}
                  />
                  
                  <Route
                    path="/inventory/exports"
                    element={<ExportsPage />}
                  />
                  
                  <Route
                    path="/inventory/inventory/inventory-list"
                    element={<MasterInventoryPage />}
                  />
                  <Route
                    path="/inventory/inventory/inventory-transactions"
                    element={<MasterInventoryPage />}
                  />
                  <Route
                    path="/inventory/inventory/inventory-configurations"
                    element={<MasterInventoryPage />}
                  />
                  {/* Redirect old path */}
                  <Route
                    path="/inventory/master"
                    element={<Navigate to={`/company/${currentCompany.id}/inventory/inventory/inventory-list`} replace />}
                  />
                         

                  <Route
                    path="/qc-tests"
                    element={<QCTests />}
                  />

                  {/* Manufacturing routes */}
                  <Route
                    path="/manufacturing/overview"
                    element={<BatchProductionOverview />}
                  />
                  <Route
                    path="/manufacturing/batches"
                    element={<BatchingPage />}
                  />
                  <Route
                    path="/manufacturing/planning"
                    element={<ProductionPlanning />}
                  />
                  <Route
                    path="/manufacturing/requests"
                    element={<Requests />}
                  />
                  <Route
                    path="/line-production"
                    element={<ProductionLinePage />}
                  />
                  <Route
                    path="/line-production/overview"
                    element={<ProductionLineOverviewPage />}
                  />
                  <Route
                    path="/line-production/overview/:lineProductionId"
                    element={<ProductionLineOverviewPage />}
                  />
                  <Route
                    path="/line-production/templates"
                    element={<ProductionLineTemplates />}
                  />
                </Routes>
              </Box>
            </Box>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

export default MainApp;

// Redirects to the same route under the authorized company ID if URL companyId differs
const CompanyLocationGuard = ({ currentCompany }) => {
  const location = useLocation();
  if (!currentCompany) return null;

  const pathname = location.pathname || '';
  if (pathname.startsWith('/company/')) {
    const segments = pathname.split('/');
    const urlCompanyId = segments[2];
    if (urlCompanyId && urlCompanyId !== currentCompany.id) {
      const newPath = ['/company', currentCompany.id, ...segments.slice(3)].join('/');
      return <Navigate to={`${newPath}${location.search}${location.hash}`} replace />;
    }
  }
  return null;
};